import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Avatar,
  Button,
  Card,
  CardContent,
  Grid,
  Fade,
  Grow,
} from "@mui/material";
import { useAuth } from "../hooks/AuthContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Rating } from "@mui/material";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CampaignIcon from "@mui/icons-material/Campaign";
import MicIcon from "@mui/icons-material/Mic";
import { ReactComponent as SharkIcon } from "../assets/icons/shark-2.svg";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { Switch, FormControlLabel } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import StarIcon from "@mui/icons-material/Star";
import AppLoader from "../components/AppLoader";
import { api } from "../services/api";
import ReplayIcon from "@mui/icons-material/Replay";
import DashboardIcon from '@mui/icons-material/Dashboard';

import AppBackground from "../components/layout/AppBackground";

const Sessions = () => {
  const { token, user } = useAuth();
  const { t, i18n } = useTranslation("sessions");
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [showOnlyEvaluated, setShowOnlyEvaluated] = useState(true);
  const [sortBy, setSortBy] = useState("date"); // "date" o "score"
  const [sortAsc, setSortAsc] = useState(false); // false = descendente por defecto

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        // const res = await fetch(`${process.env.REACT_APP_API_URL}/api/pitch-sessions/user`, {
        //   headers: { Authorization: `Bearer ${token}` },
        // });
        // const data = await res.json();
        // if (res.ok) setSessions(data.sessions);
        const res = await api.get("/api/pitch-sessions/user");
        setSessions(res.data.sessions);
      } catch (err) {
        console.error("Error fetching sessions", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSessions();
  }, [token]);

  //console.log("🔍 First session:", sessions[0]);
  const evaluatedSessions = Array.isArray(sessions)
  ? sessions.filter((s) => s.evaluated && s.global_score > 0)
  : [];

  const avgScore =
  evaluatedSessions.length > 0
    ? (
        evaluatedSessions.reduce((acc, s) => acc + (s.global_score || 0), 0) /
        evaluatedSessions.length
      ).toFixed(2)
    : "-";

  const displayedSessions = (showOnlyEvaluated
      ? sessions.filter((s) => s.evaluated && s.global_score > 0)
      : sessions
    ).slice().sort((a, b) => {
      let compareVal = 0;
      if (sortBy === "score") {
        compareVal = (a.global_score || 0) - (b.global_score || 0);
      } else {
        compareVal = new Date(a.start_time) - new Date(b.start_time);
      }
      return sortAsc ? compareVal : -compareVal;
    });

  const renderLanguageBadge = (lang) => {
    const label = lang === "es" ? "ES" : lang === "en" ? "EN" : "🌐";
    return (
      <Box
        component="span"
        sx={{
          display: "inline-block",
          px: 1,
          py: 0.2,
          borderRadius: 1,
          fontSize: "0.75rem",
          fontWeight: "bold",
          backgroundColor: "#555",
          color: "#fff",
          ml: 0.5,
        }}
      >
        {label}
      </Box>
    );
  };

  const getIconForPitchType = (name) => {
    const lower = name.toLowerCase();

    let IconComponent = RocketLaunchIcon;
    if (lower.includes("elevator")) IconComponent = TrendingUpIcon;
    else if (lower.includes("demo")) IconComponent = CampaignIcon;
    else if (lower.includes("shark")) IconComponent = () => <SharkIcon style={{ width: 22, height: 22 }} />;
    else if (lower.includes("media") || lower.includes("medio")) IconComponent = MicIcon;

    return (
      <Box display="flex" alignItems="center">
      <Box
        sx={{
          bgcolor: "#a855f7",
          color: "#fff",
          width: 32,
          height: 32,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mr: 1,
        }}
      >
        <IconComponent />
      </Box>
      <Typography variant="subtitle1">{name}</Typography>
    </Box>
    );
  };


  if (loading) return <AppLoader message={t("loading")} />;

  return (
    <>
    <AppBackground
      sx={{
        color: "#fff",
      }}
    >
      <Fade in timeout={700}>
        <Box
          sx={{
            backgroundColor: "rgba(0,0,0,0.75)",
            borderRadius: 4,
            maxWidth: 900,
            mx: "auto",
            px: { xs: 2, md: 3 },
            py: 4,
          }}
        >
          <Typography variant="h5" mb={4} fontWeight="bold" sx={{textAlign: { xs: "center", sm: "left" }}}>
            {t("title")}
          </Typography>

          {/* Resumen */}
          <Grow in timeout={800}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-around",
                textAlign: "center",
                backgroundColor: "rgba(255,255,255,0.05)",
                borderRadius: 2,
                py: 2,
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="body2">{t("total_sessions")}</Typography>
                <Typography variant="h6">{sessions.length}</Typography>
              </Box>
              <Box>
                <Typography variant="body2">{t("evaluated_sessions")}</Typography>
                <Typography variant="h6">{evaluatedSessions.length}</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="body2" gutterBottom>
                  {t("average_score")}
                </Typography>
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  <CircularProgress
                    variant="determinate"
                    value={avgScore !== "-" ? (parseFloat(avgScore) / 5) * 100 : 0}
                    size={48}
                    thickness={4}
                    sx={{ color: "#8e44ad" }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                    }}
                  >
                    <Typography variant="caption" fontWeight="bold">
                      {avgScore !== "-" ? parseFloat(avgScore).toFixed(1) : "-"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grow>
          <Grow in timeout={900}>
            <Box display="flex" justifyContent="space-flex-start" alignItems="center" mb={2} flexWrap="wrap" gap={0.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showOnlyEvaluated}
                    onChange={() => setShowOnlyEvaluated(!showOnlyEvaluated)}
                    color="primary"
                  />
                }
                label={t("show_only_evaluated")}
              />
              {/* Toggle de orden asc/desc con ícono */}
              <Button
                variant="outlined"
                onClick={() => setSortAsc((prev) => !prev)}
                sx={{
                  color: "#fff",
                  borderColor: "#a855f7",
                  backgroundColor: "rgba(168, 85, 247, 0.2)",
                  borderRadius: 10,
                  height: 34, // igual que el botón de al lado
                  minWidth: 34,
                  px: 0.5,
                  py: 0.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  "&:hover": {
                    backgroundColor: "rgba(168, 85, 247, 0.4)",
                    borderColor: "#a855f7",
                  },
                }}
              >
                {sortAsc ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
              </Button>

              {/* Toggle de tipo de orden (fecha / puntaje) */}
              <Button
                variant="outlined"
                onClick={() => setSortBy((prev) => (prev === "date" ? "score" : "date"))}
                sx={{
                  color: "#fff",
                  borderColor: "#a855f7",
                  backgroundColor: "rgba(168, 85, 247, 0.2)",
                  borderRadius: 10,
                  px: 2,
                  gap: 1,
                  "&:hover": {
                    backgroundColor: "rgba(168, 85, 247, 0.4)",
                    borderColor: "#a855f7",
                  },
                }}
              >
                {sortBy === "date" ? <CalendarTodayIcon fontSize="small" /> : <StarIcon fontSize="small" />}
                <Typography variant="body2" textTransform="none">
                  {sortBy === "date" ? t("sort_date") : t("sort_score")}
                </Typography>
              </Button>
            </Box>
          </Grow>
          {/* Lista de sesiones */}
          <Grow in timeout={1000}>
            <Grid container spacing={2}>
              {/* {sessions.map((s) => { */}
              {displayedSessions.map((s) => {

                const isEvaluated = typeof s.global_score === "number" && s.global_score > 0;

                return ( // ← ✅ FALTA ESTE RETURN
                  <Grid item xs={12} md={6} key={s.id}>
                    <Card sx={{ backgroundColor: "rgba(255, 255, 255, 0.05)" }}>
                      {/* "#1c1c1c" */}
                      <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
                          <Box display="flex" alignItems="center">
                            {getIconForPitchType(i18n.language?.startsWith("es") ? s.pitch_type_name_es : s.pitch_type_name_en)}
                          </Box>
                          <Box display="flex" alignItems="center">
                            <Typography variant="Subtitle">
                              {i18n.language?.startsWith("es") ? s.ai_profile_name_es : s.ai_profile_name_en}
                            </Typography>
                            <Avatar src={s.ai_profile_image} sx={{ width: 34, height: 34, ml: 1 }} />
                          </Box>
                        </Box>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mt={1}
                          mb={1}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            {renderLanguageBadge(s.language)}
                            {isEvaluated ? (
                              <Rating
                                value={s.global_score}
                                precision={0.1}
                                readOnly
                                size="small"
                                sx={{ color: "#FFD700" }}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ color: "#aaa" }}>
                                ---
                              </Typography>
                            )}
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <CalendarTodayIcon fontSize="small" />
                            <Typography variant="body2">
                            {new Intl.DateTimeFormat(i18n.language?.startsWith("es") ? "es-AR" : "en-US", {
                                dateStyle: "short",
                                timeStyle: "short",
                                hourCycle: "h23",
                              }).format(new Date(s.start_time))}
                            </Typography>
                          </Box>
                        </Box>
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={isEvaluated ? () => navigate(`/evaluation/${s.id}`) : undefined}
                          sx={{
                            mt: 2,
                            color: isEvaluated ? "#fff" : "#aaa",
                            borderColor: isEvaluated ? "#fff" : "#666",
                            cursor: isEvaluated ? "pointer" : "default",
                            "&:hover": {
                              borderColor: isEvaluated ? "#fff" : "#666",
                            },
                          }}
                        >
                          {isEvaluated ? t("view_evaluation") : t("not_evaluated")}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Grow>

          <Grow in timeout={1100}>
            <Box
              sx={{
                textAlign: "center",
                mt: 4,
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "center",
                alignItems: "center",
                gap: { xs: 1, sm: 2 },
              }}
            >
              <Button
                variant="outlined"
                onClick={() => navigate("/pitch-start")}
                sx={{ color: "#fff", borderColor: "#fff", gap: 1, mb: { xs: 1, sm: 0 } }}
              >
                <ReplayIcon sx={{ fontSize: 20 }} /> {t("new_session")}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/dashboard")}
                sx={{ color: "#fff", borderColor: "#fff", gap: 1, mb: { xs: 1, sm: 0 } }}
              >
                <DashboardIcon sx={{ fontSize: 20 }} /> {t("back_to_dashboard")}
              </Button>
            </Box>
          </Grow>
        </Box>
      </Fade>
    </AppBackground>
    </>
  );
};

export default Sessions;
